/**
 * Onboarding route table
 *
 * Registers all `/webAppOnboarding/*` screens behind sign-up and Firebase auth guards.
 * Screen filenames and paths are stable; meta (step labels) comes from each screen module.
 */

import { Navigate, Route, Routes } from 'react-router-dom';
import { OnboardingAuthGuard } from '../OnboardingAuthGuard';
import { OnboardingCompletedGuard } from '../OnboardingCompletedGuard';
import { OnboardingSignUpGateLayout } from '../components/layout/OnboardingSignUpGateLayout';
import {
  ONBOARDING_SCREEN_META as courseNotCertifiedScreenMeta,
  WebOnboardingCourseNotCertifiedProcessOverviewScreen,
} from '../screens/WebOnboardingCourseNotCertifiedProcessOverviewScreen';
import {
  ONBOARDING_SCREEN_META as installScreenMeta,
  WebOnboardingInstallScreen,
} from '../screens/WebOnboardingInstallScreen';
import {
  ONBOARDING_SCREEN_META as insuranceAbtretungExplainScreenMeta,
  WebOnboardingInsuranceAbtretungProcessOverviewScreen,
} from '../screens/WebOnboardingInsuranceAbtretungProcessOverviewScreen';
import {
  ONBOARDING_SCREEN_META as insuranceBnplProcessOverviewScreenMeta,
  WebOnboardingInsuranceBnplProcessOverviewScreen,
} from '../screens/WebOnboardingInsuranceBnplProcessOverviewScreen';
import {
  ONBOARDING_SCREEN_META as insuranceBnplCheckoutScreenMeta,
  WebOnboardingInsuranceBnplCheckoutScreen,
} from '../screens/WebOnboardingInsuranceBnplCheckoutScreen';
import {
  ONBOARDING_SCREEN_META as insuranceBnplPaymentScreenMeta,
  WebOnboardingInsuranceBnplPaymentScreen,
} from '../screens/WebOnboardingInsuranceBnplPaymentScreen';
import {
  ONBOARDING_SCREEN_META as insuranceBnplRejectedScreenMeta,
  WebOnboardingInsuranceBnplRejectedScreen,
} from '../screens/WebOnboardingInsuranceBnplRejectedScreen';
import {
  ONBOARDING_SCREEN_META as insuranceWithoutAbtretungPhysiofitScreenMeta,
  WebOnboardingInsuranceWithoutAbtretungPhysiofitProcessOverviewScreen,
} from '../screens/WebOnboardingInsuranceWithoutAbtretungPhysiofitProcessOverviewScreen';
import {
  ONBOARDING_SCREEN_META as insuranceWithoutAbtretungPhysiofitCheckoutScreenMeta,
  WebOnboardingInsuranceWithoutAbtretungPhysiofitCheckoutScreen,
} from '../screens/WebOnboardingInsuranceWithoutAbtretungPhysiofitCheckoutScreen';
import {
  ONBOARDING_SCREEN_META as insuranceWithoutAbtretungPatientProcessOverviewScreenMeta,
  WebOnboardingInsuranceWithoutAbtretungPatientProcessOverviewScreen,
} from '../screens/WebOnboardingInsuranceWithoutAbtretungPatientProcessOverviewScreen';
import {
  ONBOARDING_SCREEN_META as insuranceWithoutAbtretungPatientCheckoutScreenMeta,
  WebOnboardingInsuranceWithoutAbtretungPatientCheckoutScreen,
} from '../screens/WebOnboardingInsuranceWithoutAbtretungPatientCheckoutScreen';
import {
  ONBOARDING_SCREEN_META as insuranceBlockedExplainScreenMeta,
  WebOnboardingInsuranceBlockedFreeProcessOverviewScreen,
} from '../screens/WebOnboardingInsuranceBlockedFreeProcessOverviewScreen';
import {
  ONBOARDING_SCREEN_META as insuranceBlockedPracticeFeeScreenMeta,
  WebOnboardingInsuranceBlockedPracticeFeeProcessOverviewScreen,
} from '../screens/WebOnboardingInsuranceBlockedPracticeFeeProcessOverviewScreen';
import {
  ONBOARDING_SCREEN_META as insuranceBlockedTempProcessOverviewScreenMeta,
  WebOnboardingInsuranceBlockedTempProcessOverviewScreen,
} from '../screens/WebOnboardingInsuranceBlockedTempProcessOverviewScreen';
import {
  ONBOARDING_SCREEN_META as insuranceInvoiceCheckoutScreenMeta,
  WebOnboardingInsuranceInvoiceCheckoutScreen,
} from '../screens/WebOnboardingInsuranceInvoiceCheckoutScreen';
import {
  ONBOARDING_SCREEN_META as insuranceInvoiceProcessOverviewScreenMeta,
  WebOnboardingInsuranceInvoiceProcessOverviewScreen,
} from '../screens/WebOnboardingInsuranceInvoiceProcessOverviewScreen';
import {
  ONBOARDING_SCREEN_META as insuranceSelectScreenMeta,
  WebOnboardingInsuranceSelectScreen,
} from '../screens/WebOnboardingInsuranceSelectScreen';
import {
  ONBOARDING_SCREEN_META as phoneVerificationScreenMeta,
  WebOnboardingPhoneVerificationScreen,
} from '../screens/WebOnboardingPhoneVerificationScreen';
import {
  ONBOARDING_SCREEN_META as welcomeScreenMeta,
  WebOnboardingWelcomeScreen,
} from '../screens/WebOnboardingWelcomeScreen';
import { useCourseCertified } from '../hooks/useCourseCertified';
import {
  getCourseNotCertifiedPath,
  getInsuranceExplainPath,
  resolveBillingTypeFromPatient,
} from '../insurance/routing';
import { getSignedUpPatient, useOnboardingStore } from '../store/onboardingStore';

if (import.meta.env.DEV) {
  for (const [label, meta] of [
    ['WebOnboardingWelcomeScreen', welcomeScreenMeta],
    ['WebOnboardingPhoneVerificationScreen', phoneVerificationScreenMeta],
  ] as const) {
    if (meta.requiresFirebaseSession) {
      throw new Error(
        `${label}: routes outside OnboardingAuthGuard must set ONBOARDING_SCREEN_META.requiresFirebaseSession to false`,
      );
    }
  }

  for (const [label, meta] of [
    ['WebOnboardingInsuranceSelectScreen', insuranceSelectScreenMeta],
    ['WebOnboardingInsuranceInvoiceProcessOverviewScreen', insuranceInvoiceProcessOverviewScreenMeta],
    ['WebOnboardingInsuranceBlockedFreeProcessOverviewScreen', insuranceBlockedExplainScreenMeta],
    ['WebOnboardingInsuranceBlockedPracticeFeeProcessOverviewScreen', insuranceBlockedPracticeFeeScreenMeta],
    ['WebOnboardingCourseNotCertifiedProcessOverviewScreen', courseNotCertifiedScreenMeta],
    ['WebOnboardingInsuranceBlockedTempProcessOverviewScreen', insuranceBlockedTempProcessOverviewScreenMeta],
    ['WebOnboardingInsuranceAbtretungProcessOverviewScreen', insuranceAbtretungExplainScreenMeta],
    ['WebOnboardingInsuranceBnplProcessOverviewScreen', insuranceBnplProcessOverviewScreenMeta],
    ['WebOnboardingInsuranceBnplCheckoutScreen', insuranceBnplCheckoutScreenMeta],
    ['WebOnboardingInsuranceBnplPaymentScreen', insuranceBnplPaymentScreenMeta],
    ['WebOnboardingInsuranceBnplRejectedScreen', insuranceBnplRejectedScreenMeta],
    [
      'WebOnboardingInsuranceWithoutAbtretungPhysiofitProcessOverviewScreen',
      insuranceWithoutAbtretungPhysiofitScreenMeta,
    ],
    [
      'WebOnboardingInsuranceWithoutAbtretungPhysiofitCheckoutScreen',
      insuranceWithoutAbtretungPhysiofitCheckoutScreenMeta,
    ],
    [
      'WebOnboardingInsuranceWithoutAbtretungPatientProcessOverviewScreen',
      insuranceWithoutAbtretungPatientProcessOverviewScreenMeta,
    ],
    ['WebOnboardingInsuranceWithoutAbtretungPatientCheckoutScreen', insuranceWithoutAbtretungPatientCheckoutScreenMeta],
    ['WebOnboardingInsuranceInvoiceCheckoutScreen', insuranceInvoiceCheckoutScreenMeta],
    ['WebOnboardingInstallScreen', installScreenMeta],
  ] as const) {
    if (!meta.requiresFirebaseSession) {
      throw new Error(
        `${label}: routes under OnboardingAuthGuard must set ONBOARDING_SCREEN_META.requiresFirebaseSession to true`,
      );
    }
  }
}

function InsuranceExplainRedirect() {
  const signedUpPatient = useOnboardingStore(getSignedUpPatient);
  const certified = useCourseCertified(signedUpPatient?.course?.id ?? '');
  const billingType = resolveBillingTypeFromPatient(signedUpPatient);
  if (!billingType) {
    return <Navigate to="/webAppOnboarding/insurance-select" replace />;
  }
  if (certified.status === 'loading') {
    return null; // avoid flashing the wrong route until certified status resolves
  }
  const insuranceInfo = signedUpPatient?.webAppOnboarding?.insuranceInfo;
  const practiceChargesFee = insuranceInfo?.practiceChargesFee === true;
  // The not-certified screen tells the patient the course costs them nothing, which is wrong when
  // the practice charges its own fee. The fee screen wins for those patients (PF-5963).
  if (certified.status === 'ready' && certified.certified === false && !practiceChargesFee) {
    return <Navigate to={getCourseNotCertifiedPath()} replace />;
  }
  return (
    <Navigate
      to={getInsuranceExplainPath(billingType, { blockedReason: insuranceInfo?.blockedReason, practiceChargesFee })}
      replace
    />
  );
}

export const OnboardingRouter = () => {
  return (
    <Routes>
      <Route element={<OnboardingSignUpGateLayout />}>
        <Route index element={<WebOnboardingWelcomeScreen />} />
        <Route path="phone-verification" element={<WebOnboardingPhoneVerificationScreen />} />
        {/*
          Post–phone-verification steps: add <Route path="…" element={…} /> as children here.
          Each screen should export ONBOARDING_SCREEN_META with requiresFirebaseSession: true.
        */}
        <Route element={<OnboardingAuthGuard />}>
          <Route path="install" element={<WebOnboardingInstallScreen />} />
          <Route element={<OnboardingCompletedGuard />}>
            <Route path="course-explain" element={<Navigate to="/webAppOnboarding/insurance-select" replace />} />
            <Route path="insurance-select" element={<WebOnboardingInsuranceSelectScreen />} />
            <Route path="insurance-invoice-explain" element={<WebOnboardingInsuranceInvoiceProcessOverviewScreen />} />
            <Route
              path="insurance-invoice-process-overview"
              element={<Navigate to="/webAppOnboarding/insurance-invoice-explain" replace />}
            />
            <Route
              path="insurance-blocked-free-process-overview"
              element={<WebOnboardingInsuranceBlockedFreeProcessOverviewScreen />}
            />
            <Route
              path="insurance-blocked-practice-fee-process-overview"
              element={<WebOnboardingInsuranceBlockedPracticeFeeProcessOverviewScreen />}
            />
            <Route
              path="insurance-blocked-temp-process-overview"
              element={<WebOnboardingInsuranceBlockedTempProcessOverviewScreen />}
            />
            <Route
              path="course-not-certified-process-overview"
              element={<WebOnboardingCourseNotCertifiedProcessOverviewScreen />}
            />
            <Route
              path="insurance-abtretung-process-overview"
              element={<WebOnboardingInsuranceAbtretungProcessOverviewScreen />}
            />
            <Route
              path="insurance-bnpl-process-overview"
              element={<WebOnboardingInsuranceBnplProcessOverviewScreen />}
            />
            {/* Merged into the explain screen (ERS-61) — redirect resumed sessions and old links. */}
            <Route
              path="insurance-bnpl-payment-overview"
              element={<Navigate to="/webAppOnboarding/insurance-bnpl-process-overview" replace />}
            />
            <Route path="insurance-bnpl-checkout" element={<WebOnboardingInsuranceBnplCheckoutScreen />} />
            <Route path="insurance-bnpl-payment" element={<WebOnboardingInsuranceBnplPaymentScreen />} />
            <Route path="insurance-bnpl-rejected" element={<WebOnboardingInsuranceBnplRejectedScreen />} />
            <Route
              path="insurance-without-abtretung-physiofit-process-overview"
              element={<WebOnboardingInsuranceWithoutAbtretungPhysiofitProcessOverviewScreen />}
            />
            <Route
              path="insurance-without-abtretung-physiofit-checkout"
              element={<WebOnboardingInsuranceWithoutAbtretungPhysiofitCheckoutScreen />}
            />
            <Route
              path="insurance-without-abtretung-patient-process-overview"
              element={<WebOnboardingInsuranceWithoutAbtretungPatientProcessOverviewScreen />}
            />
            <Route
              path="insurance-without-abtretung-patient-checkout"
              element={<WebOnboardingInsuranceWithoutAbtretungPatientCheckoutScreen />}
            />
            <Route path="insurance-invoice-checkout" element={<WebOnboardingInsuranceInvoiceCheckoutScreen />} />
            <Route
              path="insurance-invoice-checkout-merged"
              element={<Navigate to="/webAppOnboarding/insurance-invoice-checkout" replace />}
            />
            <Route
              path="insurance-invoice-consent"
              element={<Navigate to="/webAppOnboarding/insurance-invoice-checkout" replace />}
            />
            <Route path="insurance-explain" element={<InsuranceExplainRedirect />} />
          </Route>
        </Route>
      </Route>
      <Route path="welcome" element={<Navigate to="/webAppOnboarding" replace />} />
    </Routes>
  );
};
